/**
 * QuoteBrowser.jsx — Browse and manage saved quotes
 *
 * Features:
 * - Status filter with counts
 * - Search by client name or quote number
 * - Sort by date, total, or client
 * - Date range presets
 * - Summary stats bar
 * - Rich detail panel with line items, shoot dates, revision chain
 * - Actions: Edit in Builder, Mark Sent/Accepted, Reject, Revise, Duplicate, PDF, Delete
 * - Job + contact navigation links
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuotes, useUpdateQuote, useDeleteQuote, useCreateQuote } from '../../hooks/useQuotes';
import { useJobs, useUpdateJob } from '../../hooks/useJobs';
import { useSettings } from '../../hooks/useSettings';
import { useRBAC } from '../../hooks/useRBAC';
import { useToast } from '../layout/Toast';
import { useMobile } from '../../hooks/useMobile';
import { fmtC, fmtD } from '../../lib/utils';
import { generateQuotePdf } from '../../lib/pdfGenerator';
import { buildVirtourDisplayLines } from '../../lib/virtourQuote';
import { resolveTerms } from '../../lib/quoteTerms';
import { isValidTransition } from '../../lib/statusTransitions';
import PdfPreviewModal from '../common/PdfPreviewModal';
import SendQuoteModal from './SendQuoteModal';
import FocusHide from '../shared/FocusHide';

const STATUS_COLORS = {
  draft:      { bg: '#6B7280', text: '#F9FAFB' },
  sent:       { bg: '#3B82F6', text: '#FFFFFF' },
  accepted:   { bg: '#10B981', text: '#FFFFFF' },
  rejected:   { bg: '#EF4444', text: '#FFFFFF' },
  superseded: { bg: '#F59E0B', text: '#FFFFFF' },
};

const DATE_PRESETS = [
  { key: 'all', label: 'All' },
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
  { key: 'month', label: 'Month' },
];

const SORT_OPTIONS = [
  { key: 'date', label: 'Date' },
  { key: 'total', label: 'Total' },
  { key: 'client', label: 'Client' },
];

export default function QuoteBrowser() {
  const { data: quotes = [], isLoading } = useQuotes();
  const { data: settings } = useSettings();
  const { hasAccess } = useRBAC();
  // Server deletes quotes at quotes:full only (crud.js TABLE_PERMS).
  const canDelete = hasAccess('quotes', 'full', settings);
  const { data: allJobs = [] } = useJobs();
  const updateJob = useUpdateJob();
  const updateQuote = useUpdateQuote();
  const deleteQuote = useDeleteQuote();
  const createQuoteMut = useCreateQuote();
  const { toast } = useToast();
  const { isMobile } = useMobile();
  const navigate = useNavigate();

  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [sortBy, setSortBy] = useState('date');
  const [sortAsc, setSortAsc] = useState(false);
  const [dateRange, setDateRange] = useState('all');
  const [pdfPreview, setPdfPreview] = useState(null);
  // ID of the quote currently being sent via the SendQuoteModal pre-flight.
  const [sendQuoteId, setSendQuoteId] = useState(null);

  const handleMobileBack = useCallback(() => setSelectedId(null), []);

  // ?select=<id> (navigateToQuote, e.g. a to-do's link): select it once the list
  // is in, moving on to its newest revision when it was superseded, then drop the param.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectParam = searchParams.get('select');
  useEffect(() => {
    if (!selectParam || isLoading) return;
    const childOf = new Map(quotes.filter((q) => q.parentQuoteId).map((q) => [q.parentQuoteId, q]));
    let target = quotes.find((q) => q.id === selectParam);
    const seen = new Set();
    while (target?.status === 'superseded' && childOf.has(target.id) && !seen.has(target.id)) {
      seen.add(target.id);
      target = childOf.get(target.id);
    }
    if (target) setSelectedId(target.id);
    const next = new URLSearchParams(searchParams);
    next.delete('select');
    setSearchParams(next, { replace: true });
  }, [selectParam, isLoading, quotes, searchParams, setSearchParams]);

  // ── Date range cutoff ──
  const dateCutoff = useMemo(() => {
    const now = new Date();
    if (dateRange === '7d') return new Date(now.getTime() - 7 * 86400000);
    if (dateRange === '30d') return new Date(now.getTime() - 30 * 86400000);
    if (dateRange === 'month') return new Date(now.getFullYear(), now.getMonth(), 1);
    return null;
  }, [dateRange]);

  // ── Filtered + sorted quotes ──
  const filtered = useMemo(() => {
    let list = quotes;
    // Hide superseded quotes by default — they're visible in revision chains
    if (filter === 'all') list = list.filter((q) => q.status !== 'superseded');
    else list = list.filter((q) => q.status === filter);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((q) =>
        (q.clientName || '').toLowerCase().includes(s) ||
        (q.quoteNumber || '').toLowerCase().includes(s)
      );
    }
    if (dateCutoff) {
      list = list.filter((q) => new Date(q.createdAt) >= dateCutoff);
    }
    const sorted = [...list].sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'date') cmp = new Date(b.createdAt) - new Date(a.createdAt);
      else if (sortBy === 'total') cmp = (b.total || 0) - (a.total || 0);
      else if (sortBy === 'client') cmp = (a.clientName || '').localeCompare(b.clientName || '');
      return sortAsc ? -cmp : cmp;
    });
    return sorted;
  }, [quotes, filter, search, dateCutoff, sortBy, sortAsc]);

  const selected = quotes.find((q) => q.id === selectedId);

  // ── Summary stats (unique deals only — latest revision per chain) ──
  const stats = useMemo(() => {
    // A quote is the "latest" in its chain if no other quote has it as parentQuoteId
    const childParentIds = new Set(quotes.filter(q => q.parentQuoteId).map(q => q.parentQuoteId));
    const latestQuotes = quotes.filter(q => !childParentIds.has(q.id));
    const accepted = latestQuotes.filter(q => q.status === 'accepted');
    const acceptedTotal = accepted.reduce((s, q) => s + (parseFloat(q.total) || 0), 0);
    const avgValue = accepted.length > 0 ? acceptedTotal / accepted.length : 0;
    // Win rate: accepted / (accepted + rejected + draft + sent) — only latest per chain
    const actionable = latestQuotes.filter(q => q.status !== 'superseded');
    const conversionRate = actionable.length > 0 ? ((accepted.length / actionable.length) * 100) : 0;
    return { acceptedTotal, avgValue, conversionRate, acceptedCount: accepted.length, dealCount: latestQuotes.length };
  }, [quotes]);

  // ── Status change ──
  const handleStatusChange = async (id, newStatus) => {
    try {
      const updates = {
        id,
        status: newStatus,
        ...(newStatus === 'sent' ? { sentAt: new Date().toISOString() } : {}),
        ...(newStatus === 'accepted' ? { acceptedAt: new Date().toISOString() } : {}),
      };
      await updateQuote.mutateAsync(updates);

      // Reverse sync: Mark Accepted → job confirmed
      if (newStatus === 'accepted') {
        const quote = quotes.find(q => q.id === id);
        if (quote?.jobId) {
          const job = allJobs.find(j => j.id === quote.jobId);
          if (job && isValidTransition(job.status, 'confirmed')) {
            try { await updateJob.mutateAsync({ id: job.id, status: 'confirmed' }); } catch { /* non-critical */ }
          }
        }
      }

      toast(`Quote marked as ${newStatus}`);
    } catch (err) {
      toast(`Failed to update: ${err.message}`, 'error');
    }
  };

  // ── Delete ──
  const handleDelete = async (id) => {
    const quote = quotes.find(q => q.id === id);
    // Block deletion of sent/accepted quotes linked to a job
    if (quote?.jobId && (quote.status === 'sent' || quote.status === 'accepted')) {
      toast('Cannot delete a sent or accepted quote linked to a job. Void the job first or revise the quote.', 'error');
      return;
    }
    if (!confirm('Delete this quote? This cannot be undone.')) return;
    try {
      await deleteQuote.mutateAsync(id);
      if (selectedId === id) setSelectedId(null);
      toast('Quote deleted');
    } catch (err) {
      toast(`Failed to delete: ${err.message}`, 'error');
    }
  };

  // ── Check if quote already has a child revision ──
  const hasChildRevision = useCallback((quoteId) => {
    return quotes.some(q => q.parentQuoteId === quoteId);
  }, [quotes]);

  // ── Revise ──
  const handleRevise = async (quote) => {
    if (hasChildRevision(quote.id)) {
      toast('This quote already has a revision. Edit or revise the latest version instead.', 'error');
      return;
    }
    try {
      const baseNumber = quote.quoteNumber.replace(/-R\d+$/, '');
      const revised = await createQuoteMut.mutateAsync({
        quoteNumber: `${baseNumber}-R${(quote.revision || 1) + 1}`,
        clientName: quote.clientName,
        clientEmail: quote.clientEmail,
        contactId: quote.contactId,
        jobId: quote.jobId,
        mode: quote.mode,
        workflowType: quote.workflowType || 'standard',
        status: 'draft',
        deliverableLines: quote.deliverableLines,
        addOns: quote.addOns,
        shootDates: quote.shootDates,
        subtotal: quote.subtotal,
        discountPct: quote.discountPct,
        discountAmt: quote.discountAmt,
        discountReason: quote.discountReason,
        total: quote.total,
        terms: quote.terms,
        notes: quote.notes,
        calcSnapshot: quote.calcSnapshot,
        revision: (quote.revision || 1) + 1,
        parentQuoteId: quote.id,
      });
      await updateQuote.mutateAsync({ id: quote.id, status: 'superseded' });
      setSelectedId(revised.id);
      toast(`Revision v${(quote.revision || 1) + 1} created`);
    } catch (err) {
      toast(`Failed to revise: ${err.message}`, 'error');
    }
  };

  // ── Duplicate as New ──
  const handleDuplicate = async (quote) => {
    try {
      const prefix = settings?.data?.quotePrefix || 'RF-Q';
      const year = new Date().getFullYear();
      let maxSeq = (parseInt(settings?.data?.quoteStart, 10) || 1) - 1;
      const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const pattern = new RegExp(`^${escaped}-${year}-(\\d+)(?:-R\\d+)?$`);
      for (const q of quotes) {
        const match = q.quoteNumber?.match(pattern);
        if (match) maxSeq = Math.max(maxSeq, parseInt(match[1]));
      }
      const seq = String(maxSeq + 1).padStart(3, '0');
      const newNumber = `${prefix}-${year}-${seq}`;

      // Duplicate targets a different future job — the source job's pay data
      // (splits, crew, per-item allocations) must not travel with it
      const {
        payMode, itemAllocs, itemSplits, originators, originatorRemainder,
        prod, post, splits, amounts, redistribution, savedAt, expenseMeta,
        ...cleanSnapshot
      } = quote.calcSnapshot || {};

      const dup = await createQuoteMut.mutateAsync({
        quoteNumber: newNumber,
        clientName: quote.clientName,
        clientEmail: quote.clientEmail,
        contactId: quote.contactId,
        mode: quote.mode,
        workflowType: quote.workflowType || 'standard',
        status: 'draft',
        deliverableLines: quote.deliverableLines,
        addOns: quote.addOns,
        shootDates: quote.shootDates,
        subtotal: quote.subtotal,
        discountPct: quote.discountPct,
        discountAmt: quote.discountAmt,
        discountReason: quote.discountReason,
        total: quote.total,
        terms: quote.terms,
        notes: quote.notes,
        calcSnapshot: cleanSnapshot,
        revision: 1,
      });
      setSelectedId(dup.id);
      toast(`Duplicated as ${newNumber}`);
    } catch (err) {
      toast(`Failed to duplicate: ${err.message}`, 'error');
    }
  };

  // ── Edit in Builder ──
  const handleEditInBuilder = (quote) => {
    navigate(`/quotes?tab=builder&quoteId=${quote.id}`);
  };

  // ── Build PDF args from a saved quote (shared by Download + Preview) ──
  const buildQuotePdfArgs = (quote) => {
    const isVirtour = quote.mode === 'virtour';
    const rawLines = (quote.deliverableLines || []).map((dl, i) => ({
      id: i + 1,
      desc: dl.desc || dl.name || '',
      detail: dl.detail || '',
      qty: dl.qty ?? 1,
      rate: dl.rate ?? dl.price ?? dl.amount ?? 0,
      dur: dl.dur || 0,
      source: dl.source || quote.mode,
      ...(isVirtour ? { splitPct: dl.splitPct ?? null } : {}),
      schedule: Array.isArray(dl.schedule) ? dl.schedule : [],
      deliveryDate: dl.deliveryDate || '',
      deliveryTime: dl.deliveryTime || '',
      on: true,
    }));
    // VirTour: printed row amounts are our payout, so they sum to the quote total.
    const lines = isVirtour ? buildVirtourDisplayLines(rawLines) : rawLines;
    const subtotal = lines.reduce((s, l) => s + (l.qty || 1) * (l.rate || 0), 0);
    const discAmt = parseFloat(quote.discountAmt) || 0;
    const discPct = parseFloat(quote.discountPct) || 0;
    const discountAmount = discAmt || (discPct ? subtotal * discPct / 100 : 0);
    const total = subtotal - discountAmount;
    const state = {
      quoteNumber: quote.quoteNumber || '',
      quoteDate: quote.createdAt ? quote.createdAt.split('T')[0] : '',
      validUntil: quote.expiresAt ? quote.expiresAt.split('T')[0] : '',
      client: quote.clientName || '',
      email: quote.clientEmail || '',
      phone: '',
      address: quote.calcSnapshot?.location || '',
      originator: quote.calcSnapshot?.originator || '',
      notes: quote.notes || '',
      mode: quote.mode || 'custom',
      lines,
      discountType: discPct > 0 ? 'pct' : 'flat',
      discountValue: discPct > 0 ? discPct : (discAmt || 0),
      discountReason: quote.discountReason || '',
    };
    const computed = { subtotal, discountAmount, total, lineCount: lines.length, estimatedDuration: 0 };
    // Handles both legacy string[] (IDs → looked up in DEFAULT_TERMS + settings
    // overrides) and new object[] snapshots (used as-is for immutability).
    const activeTerms = resolveTerms(quote, settings);
    return { state, computed, activeTerms, settings };
  };

  // ── Download PDF from saved quote ──
  const handleDownloadPdf = async (quote) => {
    try {
      await generateQuotePdf(buildQuotePdfArgs(quote));
      toast('PDF downloaded');
    } catch (err) {
      toast(`PDF failed: ${err.message}`, 'error');
    }
  };

  // ── Preview PDF from saved quote ──
  const handlePreviewPdf = async (quote) => {
    try {
      const r = await generateQuotePdf({ ...buildQuotePdfArgs(quote), returnBlob: true });
      setPdfPreview(r);
    } catch (err) {
      toast(`PDF failed: ${err.message}`, 'error');
    }
  };

  // ── Navigate to linked job ──
  const handleViewJob = (jobId) => {
    navigate(`/jobs?select=${jobId}`);
  };

  // ── Navigate to contact ──
  const handleViewContact = (contactId) => {
    navigate(`/people?select=${contactId}`);
  };

  // ── Revision chain ──
  const revisionChain = useMemo(() => {
    if (!selected) return [];
    const chain = [];
    const byId = new Map(quotes.map(q => [q.id, q]));
    let rootId = selected.id;
    let current = selected;
    while (current?.parentQuoteId && byId.has(current.parentQuoteId)) {
      current = byId.get(current.parentQuoteId);
      rootId = current.id;
    }
    const childMap = new Map();
    quotes.forEach(q => { if (q.parentQuoteId) childMap.set(q.parentQuoteId, q); });
    current = byId.get(rootId);
    while (current) {
      chain.push(current);
      current = childMap.get(current.id);
    }
    return chain;
  }, [selected, quotes]);

  // ── Extract earliest shoot date ──
  const getShootDate = useCallback((q) => {
    const dates = q.shootDates || [];
    if (dates.length > 0) {
      const d = typeof dates[0] === 'string' ? dates[0] : dates[0]?.date;
      if (d) return d;
    }
    const lines = q.deliverableLines || [];
    for (const l of lines) {
      for (const s of (l.schedule || [])) {
        if (s.date) return s.date;
      }
      if (l.shootDate) return l.shootDate;
    }
    return null;
  }, []);

  if (isLoading) {
    return <div className="flex items-center justify-center h-full text-text-dim text-sm">Loading quotes...</div>;
  }

  const showMobileDetail = isMobile && selected;

  if (showMobileDetail) {
    return (
      <div className="flex flex-col gap-3 min-h-0">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={handleMobileBack} sx={{ color: 'text.secondary' }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Quotes</Typography>
        </Box>
        {renderDetailPanel()}
        <PdfPreviewModal
          open={!!pdfPreview}
          blob={pdfPreview?.blob}
          filename={pdfPreview?.filename}
          onClose={() => setPdfPreview(null)}
        />
        {sendQuoteId && (
          <SendQuoteModal
            quote={quotes.find((q) => q.id === sendQuoteId)}
            job={(() => {
              const q = quotes.find((q) => q.id === sendQuoteId);
              return q?.jobId ? allJobs.find((j) => j.id === q.jobId) : null;
            })()}
            onClose={() => setSendQuoteId(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex gap-4 h-full min-h-0">
      {/* ── LEFT: List Panel ── */}
      <div className="w-full md:w-1/2 lg:w-2/5 flex flex-col gap-3 overflow-y-auto">
        {/* Summary Stats */}
        <div className="flex gap-3 flex-wrap">
          <div className="flex-1 min-w-[120px] bg-surface border border-border rounded-lg p-2.5">
            <div className="text-2xs font-semibold text-text-dim uppercase">Accepted Revenue</div>
            <div className="text-lg font-heading text-accent">{fmtC(stats.acceptedTotal)}</div>
          </div>
          <div className="flex-1 min-w-[80px] bg-surface border border-border rounded-lg p-2.5">
            <div className="text-2xs font-semibold text-text-dim uppercase">Avg Value</div>
            <div className="text-lg font-heading text-text-primary">{fmtC(stats.avgValue)}</div>
          </div>
          <div className="flex-1 min-w-[80px] bg-surface border border-border rounded-lg p-2.5">
            <div className="text-2xs font-semibold text-text-dim uppercase">Win Rate</div>
            <div className="text-lg font-heading text-text-primary">{stats.conversionRate.toFixed(0)}%</div>
          </div>
        </div>

        {/* Status Filters */}
        <div className="flex gap-2 flex-wrap">
          {['all', 'draft', 'sent', 'accepted', 'rejected'].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                filter === s ? 'bg-accent text-white' : 'bg-surface border border-border text-text-muted hover:text-text-primary'
              }`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
              {s !== 'all' && <span className="ml-1 opacity-70">({quotes.filter((q) => q.status === s).length})</span>}
            </button>
          ))}
        </div>

        {/* Search + Sort + Date Range */}
        <input
          type="text"
          placeholder="Search by client or quote #..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-3 py-2 bg-bg border border-border rounded text-sm text-text-primary placeholder-text-dim focus:outline-none focus:border-accent"
        />
        <div className="flex gap-2 flex-wrap items-center">
          <div className="flex items-center gap-1">
            <span className="text-2xs text-text-dim uppercase font-semibold">Sort</span>
            {SORT_OPTIONS.map((o) => (
              <button
                key={o.key}
                onClick={() => {
                  if (sortBy === o.key) setSortAsc(!sortAsc);
                  else { setSortBy(o.key); setSortAsc(false); }
                }}
                className={`px-2 py-0.5 text-2xs rounded transition-colors ${
                  sortBy === o.key ? 'bg-accent/20 text-accent font-semibold' : 'text-text-dim hover:text-text-muted'
                }`}
              >
                {o.label}{sortBy === o.key ? (sortAsc ? ' ↑' : ' ↓') : ''}
              </button>
            ))}
          </div>
          <div className="w-px h-4 bg-border mx-1" />
          <div className="flex items-center gap-1">
            {DATE_PRESETS.map((p) => (
              <button
                key={p.key}
                onClick={() => setDateRange(p.key)}
                className={`px-2 py-0.5 text-2xs rounded transition-colors ${
                  dateRange === p.key ? 'bg-accent/20 text-accent font-semibold' : 'text-text-dim hover:text-text-muted'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Quote Cards */}
        {filtered.length === 0 ? (
          <div className="text-center text-text-dim text-sm py-8">No quotes found</div>
        ) : (
          <div className="space-y-2">
            {filtered.map((q) => {
              const colors = STATUS_COLORS[q.status] || STATUS_COLORS.draft;
              const lineCount = Array.isArray(q.deliverableLines) ? q.deliverableLines.length : 0;
              const shootDate = getShootDate(q);
              return (
                <button
                  key={q.id}
                  data-track="quoteBrowser.row"
                  onClick={() => setSelectedId(q.id)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    selectedId === q.id
                      ? 'border-accent bg-accent/5'
                      : 'border-border bg-surface hover:bg-surface-2'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-semibold text-text-primary truncate">
                        {q.quoteNumber || 'No Number'}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded text-2xs font-semibold shrink-0 ${
                        q.mode === 're'
                          ? 'bg-blue-500/15 text-blue-400'
                          : q.mode === 'virtour'
                            ? 'bg-amber-500/15 text-amber-400'
                            : 'bg-purple-500/15 text-purple-400'
                      }`}>
                        {q.mode === 're' ? 'RE' : q.mode === 'virtour' ? 'VirTour' : 'Custom'}
                      </span>
                      {q.jobId && (
                        <span className="text-2xs text-green shrink-0" title="Linked to job">●</span>
                      )}
                    </div>
                    <span
                      className="px-2 py-0.5 rounded text-2xs font-semibold uppercase shrink-0"
                      style={{ backgroundColor: colors.bg + '20', color: colors.bg }}
                    >
                      {q.status}
                    </span>
                  </div>
                  <div className="text-xs text-text-muted truncate">{q.clientName || '—'}</div>
                  <div className="flex items-center justify-between mt-1 gap-2">
                    <div className="flex items-center gap-2 text-xs text-text-dim">
                      <span>{fmtD(q.createdAt)}</span>
                      {lineCount > 0 && <span className="opacity-70">· {lineCount} item{lineCount !== 1 ? 's' : ''}</span>}
                      {shootDate && <span className="opacity-70">· {fmtD(shootDate)}</span>}
                    </div>
                    <span className="text-sm font-heading text-accent shrink-0">{fmtC(q.total)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── RIGHT: Detail Panel (desktop) ── */}
      <div className="hidden md:flex flex-1 min-w-0">
        {selected ? renderDetailPanel() : (
          <div className="flex items-center justify-center h-full w-full">
            <p className="text-text-dim text-sm">Select a quote to view details</p>
          </div>
        )}
      </div>
      <PdfPreviewModal
        open={!!pdfPreview}
        blob={pdfPreview?.blob}
        filename={pdfPreview?.filename}
        onClose={() => setPdfPreview(null)}
      />
      {sendQuoteId && (
        <SendQuoteModal
          quote={quotes.find((q) => q.id === sendQuoteId)}
          job={(() => {
            const q = quotes.find((q) => q.id === sendQuoteId);
            return q?.jobId ? allJobs.find((j) => j.id === q.jobId) : null;
          })()}
          onClose={() => setSendQuoteId(null)}
        />
      )}
    </div>
  );

  function renderDetailPanel() {
    if (!selected) return null;
    // VirTour: a row's amount is our payout, not VirTour's retail, so the rows
    // add up to the total shown above them.
    const lines = selected.mode === 'virtour'
      ? buildVirtourDisplayLines(selected.deliverableLines || [])
      : (selected.deliverableLines || []);

    // Collect all shoot dates from lines
    const allShootDates = [];
    for (const l of lines) {
      for (const s of (l.schedule || [])) {
        if (s.date) allShootDates.push({ date: s.date, endDate: s.endDate, time: s.time, endTime: s.endTime, location: s.location, service: l.desc || l.name });
      }
    }

    return (
      <div className="card p-6 flex flex-col gap-4 overflow-y-auto w-full">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-heading text-text-primary">{selected.quoteNumber || 'Quote'}</h2>
            <p className="text-sm text-text-dim">
              {selected.contactId ? (
                <button data-track="quoteBrowser.client" onClick={() => handleViewContact(selected.contactId)} className="hover:text-accent transition-colors underline decoration-dotted">
                  {selected.clientName}
                </button>
              ) : selected.clientName}
            </p>
            {selected.clientEmail && <p className="text-xs text-text-dim">{selected.clientEmail}</p>}
          </div>
          <span
            className="px-3 py-1 rounded text-xs font-semibold uppercase"
            style={{
              backgroundColor: (STATUS_COLORS[selected.status]?.bg || '#999') + '20',
              color: STATUS_COLORS[selected.status]?.bg || '#999',
            }}
          >
            {selected.status}
          </span>
        </div>

        {/* Job Link */}
        {selected.jobId && (
          <button
            onClick={() => handleViewJob(selected.jobId)}
            className="flex items-center gap-2 px-3 py-2 bg-green/5 border border-green/20 rounded text-sm text-green hover:bg-green/10 transition-colors self-start"
          >
            <span className="text-xs">📋</span>
            <span>View Linked Job</span>
          </button>
        )}

        {/* Financials */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-surface p-3 rounded border border-border">
            <div className="text-2xs font-semibold text-text-dim uppercase mb-1">Subtotal</div>
            <div className="text-lg font-heading">{fmtC(selected.subtotal)}</div>
          </div>
          {parseFloat(selected.discountAmt) > 0 && (
            <div className="bg-surface p-3 rounded border border-border">
              <div className="text-2xs font-semibold text-text-dim uppercase mb-1">Discount</div>
              <div className="text-lg font-heading text-red-accent">-{fmtC(selected.discountAmt)}</div>
              {selected.discountReason && <div className="text-2xs text-text-dim mt-1">{selected.discountReason}</div>}
            </div>
          )}
          <div className="bg-surface p-3 rounded border border-border">
            <div className="text-2xs font-semibold text-text-dim uppercase mb-1">Total</div>
            <div className="text-lg font-heading text-accent">{fmtC(selected.total)}</div>
          </div>
        </div>

        {/* Line Items — enriched */}
        {lines.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-text-dim uppercase mb-2">Line Items</h3>
            <div className="space-y-1">
              {lines.map((line, i) => {
                const qty = line.qty ?? 1;
                const rate = line.rate ?? line.price ?? line.amount ?? 0;
                const amount = qty * rate;
                return (
                  <div key={i} className="py-2 px-3 bg-surface rounded border border-border/50 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-text-primary truncate">{line.desc || line.description || line.name || `Item ${i + 1}`}</span>
                      <span className="text-text-muted font-medium shrink-0">{fmtC(amount)}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-2xs text-text-dim">
                      {line.vtNote ? <span>{line.vtNote}</span> : qty !== 1 && <span>{qty} × {fmtC(rate)}</span>}
                      {line.source === 'custom' && <span className="text-purple-400">custom</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Shoot Dates */}
        {allShootDates.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-text-dim uppercase mb-2">Scheduled Shoots</h3>
            <div className="space-y-1">
              {allShootDates.map((s, i) => (
                <div key={i} className="flex items-center gap-3 py-1.5 px-3 bg-surface rounded border border-border/50 text-sm">
                  <span className="text-text-primary">{fmtD(s.date)}{s.endDate ? ` – ${fmtD(s.endDate)}` : ''}</span>
                  {s.time && <span className="text-text-dim text-xs">{s.time}{s.endTime ? `–${s.endTime}` : ''}</span>}
                  {s.location && <span className="text-text-dim text-xs truncate">@ {s.location}</span>}
                  {s.service && <span className="text-2xs text-text-dim opacity-60 truncate ml-auto">{s.service}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Meta */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          <div>
            <div className="text-2xs font-semibold text-text-dim uppercase mb-1">Mode</div>
            <p className="text-text-primary">{selected.mode === 're' ? 'Real Estate' : selected.mode === 'virtour' ? 'VirTour' : 'Custom'}</p>
          </div>
          <div>
            <div className="text-2xs font-semibold text-text-dim uppercase mb-1">Revision</div>
            <p className="text-text-primary">v{selected.revision || 1}</p>
          </div>
          <div>
            <div className="text-2xs font-semibold text-text-dim uppercase mb-1">Created</div>
            <p className="text-text-primary">{fmtD(selected.createdAt)}</p>
          </div>
          {selected.sentAt && (
            <div>
              <div className="text-2xs font-semibold text-text-dim uppercase mb-1">Sent</div>
              <p className="text-text-primary">{fmtD(selected.sentAt)}</p>
            </div>
          )}
          {selected.acceptedAt && (
            <div>
              <div className="text-2xs font-semibold text-text-dim uppercase mb-1">Accepted</div>
              <p className="text-text-primary">{fmtD(selected.acceptedAt)}</p>
            </div>
          )}
        </div>

        {selected.notes && (
          <div>
            <div className="text-2xs font-semibold text-text-dim uppercase mb-1">Notes</div>
            <p className="text-sm text-text-primary bg-surface p-3 rounded border border-border">{selected.notes}</p>
          </div>
        )}

        {/* Revision Chain — enriched */}
        {revisionChain.length > 1 && (
          <div>
            <div className="text-2xs font-semibold text-text-dim uppercase mb-2">Revision History</div>
            <div className="space-y-1">
              {revisionChain.map((q, i) => {
                const prevTotal = i > 0 ? (parseFloat(revisionChain[i - 1].total) || 0) : null;
                const delta = prevTotal !== null ? (parseFloat(q.total) || 0) - prevTotal : null;
                return (
                  <button
                    key={q.id}
                    onClick={() => setSelectedId(q.id)}
                    className={`w-full flex items-center justify-between px-3 py-1.5 rounded text-xs border transition-colors ${
                      q.id === selected.id
                        ? 'border-accent bg-accent/10 text-accent font-semibold'
                        : 'border-border bg-surface text-text-muted hover:bg-surface-2'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span>v{q.revision || 1} — {q.quoteNumber || 'Draft'}</span>
                      <span className="text-text-dim">{fmtD(q.createdAt)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {delta !== null && delta !== 0 && (
                        <span className={delta > 0 ? 'text-green' : 'text-red-accent'}>
                          {delta > 0 ? '+' : ''}{fmtC(delta)}
                        </span>
                      )}
                      <span className="uppercase">{q.status}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions — status-aware rules:
            draft  → Edit in Builder, Mark Sent, Mark Accepted
            sent   → Mark Accepted, Reject, Revise
            accepted → Revise (creates new draft, expires this one)
            rejected/superseded → (no status actions; can still Duplicate/PDF/Delete)
        */}
        <div className="flex gap-2 flex-wrap pt-2 border-t border-border">
          {selected.status === 'draft' && (
            <button onClick={() => handleEditInBuilder(selected)} className="px-3 py-1.5 bg-accent/10 border border-accent/30 text-accent text-xs font-semibold rounded hover:bg-accent/20">
              Edit in Builder
            </button>
          )}
          <FocusHide k="quoteBrowser.actions">
          {selected.status === 'draft' && (
            <button onClick={() => handleStatusChange(selected.id, 'sent')} className="px-3 py-1.5 bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs font-semibold rounded hover:bg-blue-500/20">
              Mark Sent
            </button>
          )}
          {(selected.status === 'draft' || selected.status === 'sent') && (
            <button
              onClick={() => setSendQuoteId(selected.id)}
              disabled={!selected.clientEmail}
              title={selected.clientEmail ? '' : 'Add a client email to send'}
              className="px-3 py-1.5 bg-accent text-bg text-xs font-semibold rounded hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {selected.status === 'sent' ? '✉ Resend' : '✉ Send Quote'}
            </button>
          )}
          {(selected.status === 'sent' || selected.status === 'draft') && (
            <button onClick={() => handleStatusChange(selected.id, 'accepted')} className="px-3 py-1.5 bg-green/10 border border-green/30 text-green text-xs font-semibold rounded hover:bg-green/20">
              Mark Accepted
            </button>
          )}
          {selected.status === 'sent' && (
            <button onClick={() => handleStatusChange(selected.id, 'rejected')} className="px-3 py-1.5 bg-red-accent/10 border border-red-accent/30 text-red-accent text-xs font-semibold rounded hover:bg-red-accent/20">
              Reject
            </button>
          )}
          </FocusHide>
          {(selected.status === 'sent' || selected.status === 'accepted') && !hasChildRevision(selected.id) && (
            <button
              onClick={() => handleRevise(selected)}
              disabled={createQuoteMut.isPending}
              className="px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-semibold rounded hover:bg-amber-500/20 disabled:opacity-50"
            >
              Revise
            </button>
          )}
          <FocusHide k="quoteBrowser.actions">
          <button
            onClick={() => handleDuplicate(selected)}
            disabled={createQuoteMut.isPending}
            className="px-3 py-1.5 bg-purple-500/10 border border-purple-500/30 text-purple-400 text-xs font-semibold rounded hover:bg-purple-500/20 disabled:opacity-50"
          >
            Duplicate
          </button>
          </FocusHide>
          <button onClick={() => handlePreviewPdf(selected)} className="px-3 py-1.5 bg-surface border border-border text-text-muted text-xs font-semibold rounded hover:bg-surface-2">
            Preview
          </button>
          <button onClick={() => handleDownloadPdf(selected)} className="px-3 py-1.5 bg-surface border border-border text-text-muted text-xs font-semibold rounded hover:bg-surface-2">
            PDF ↓
          </button>
          {canDelete && (
          <FocusHide k="quoteBrowser.delete">
          <button onClick={() => handleDelete(selected.id)} disabled={deleteQuote.isPending} className="px-3 py-1.5 bg-surface border border-border text-text-muted text-xs font-semibold rounded hover:bg-surface-2 disabled:opacity-50">
            Delete
          </button>
          </FocusHide>
          )}
        </div>
      </div>
    );
  }
}
